import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import {
  type ArBrief,
  briefDetail,
  enterBrief,
  briefs as fetchBriefs,
  mostScouted,
  pickBriefWinner,
} from '~/lib/ar';
import { getSession, useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';
import { WaveformPlayer } from '~/ui/WaveformPlayer';
import { Badge } from '~/ui/components/badge';
import { Button } from '~/ui/components/button';

// Combined A&R hub for producers: open label Briefs + the Most Scouted board,
// on one page. /scouted redirects here. The A&R dashboard (/ar) is separate
// (scout-only tools).
export const Route = createFileRoute('/briefs')({
  beforeLoad: async ({ location }) => {
    const session = await getSession();
    if (!session) throw redirect({ to: '/auth/sign-in', search: { next: location.pathname } });
  },
  head: () =>
    buildHead({
      title: 'A&R',
      description:
        'Get scouted on Prod Battle: open label briefs to enter, and the Most Scouted board of producers A&R are watching.',
      path: '/briefs',
    }),
  component: ArHub,
});

const TOC = [
  { id: 'briefs', label: 'Briefs' },
  { id: 'scouted', label: 'Most Scouted' },
];

function ArHub() {
  return (
    <section className="mx-auto max-w-3xl px-4 py-10 space-y-8">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">A&R</h1>
        <p className="text-sm text-muted-foreground">
          Where labels find producers. Enter a brief, climb the Most Scouted board, get heard.
        </p>
        <nav className="flex flex-wrap gap-2 pt-1 text-xs">
          {TOC.map((t) => (
            <a
              key={t.id}
              href={`#${t.id}`}
              className="rounded-full border border-border px-3 py-1 text-muted-foreground hover:border-primary hover:text-foreground"
            >
              {t.label}
            </a>
          ))}
        </nav>
      </header>

      <BriefsSection />
      <ScoutedSection />
    </section>
  );
}

// ─── Briefs ──────────────────────────────────────────────────────────────────

function BriefsSection() {
  const { data, isLoading } = useQuery({ queryKey: ['briefs'], queryFn: fetchBriefs });
  const items = data?.items ?? [];
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div id="briefs" className="scroll-mt-24 space-y-3">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">A&R Briefs</h2>
        <p className="text-sm text-muted-foreground">
          Labels post a brief; you make the beat. Win and an A&R hears it first.
        </p>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
      {!isLoading && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No open briefs right now. Check back soon.
        </div>
      )}

      <ul className="space-y-3">
        {items.map((b) => (
          <BriefCard
            key={b.id}
            brief={b}
            open={openId === b.id}
            onToggle={() => setOpenId(openId === b.id ? null : b.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function BriefCard({
  brief,
  open,
  onToggle,
}: {
  brief: ArBrief;
  open: boolean;
  onToggle: () => void;
}) {
  const { data: session } = useSession();
  const qc = useQueryClient();
  const { data: detail } = useQuery({
    queryKey: ['brief', brief.id],
    queryFn: () => briefDetail(brief.id),
    enabled: open,
  });
  const [audioUrl, setAudioUrl] = useState('');
  const [title, setTitle] = useState('');

  const enter = useMutation({
    mutationFn: () => enterBrief(brief.id, { audioUrl, title: title || undefined }),
    onSuccess: () => {
      setAudioUrl('');
      setTitle('');
      qc.invalidateQueries({ queryKey: ['brief', brief.id] });
      qc.invalidateQueries({ queryKey: ['briefs'] });
    },
  });
  const pick = useMutation({
    mutationFn: (submissionId: string) => pickBriefWinner(brief.id, submissionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brief', brief.id] }),
  });

  const isOwner = session?.handle === brief.arHandle;
  const deadline = new Date(brief.deadline);

  return (
    <li className="rounded-lg border border-border bg-card p-4 space-y-3">
      <button type="button" className="w-full text-left" onClick={onToggle}>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{brief.title}</span>
              {brief.genre && <Badge variant="secondary">{brief.genre.name}</Badge>}
              <Badge variant="outline">{brief.status}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              by @{brief.arHandle} · {brief.entries} entries · closes{' '}
              {deadline.toLocaleDateString()}
              {brief.bpmHint ? ` · ${brief.bpmHint}` : ''}
              {brief.reward ? ` · reward: ${brief.reward}` : ''}
            </p>
          </div>
        </div>
      </button>

      {open && (
        <div className="space-y-4 border-t border-border pt-3">
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{brief.description}</p>

          {brief.status === 'open' && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="text-sm font-medium">Enter this brief</div>
              <input
                value={audioUrl}
                onChange={(e) => setAudioUrl(e.target.value)}
                placeholder="Track URL (.mp3 / .wav / .ogg)"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title (optional)"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
              <Button
                size="sm"
                disabled={!audioUrl || enter.isPending}
                onClick={() => enter.mutate()}
              >
                {enter.isPending ? 'Submitting...' : 'Submit entry'}
              </Button>
            </div>
          )}

          <div className="space-y-2">
            <div className="text-sm font-medium">Entries ({detail?.entries.length ?? 0})</div>
            {detail?.entries.map((e) => (
              <div key={e.id} className="rounded-md border border-border p-2 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm">
                    @{e.producerHandle}
                    {e.title ? ` - ${e.title}` : ''}
                  </span>
                  {e.isWinner ? (
                    <Badge className="bg-violet-400/15 text-violet-300">winner</Badge>
                  ) : (
                    isOwner &&
                    brief.status !== 'closed' && (
                      <Button size="sm" variant="outline" onClick={() => pick.mutate(e.id)}>
                        Pick winner
                      </Button>
                    )
                  )}
                </div>
                <WaveformPlayer src={e.audioUrl} />
              </div>
            ))}
          </div>
        </div>
      )}
    </li>
  );
}

// ─── Most Scouted ────────────────────────────────────────────────────────────

function ScoutedSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['most-scouted'],
    queryFn: () => mostScouted(50),
  });
  const items = data?.items ?? [];

  return (
    <div id="scouted" className="scroll-mt-24 space-y-3 border-t border-border pt-8">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Most Scouted</h2>
        <p className="text-sm text-muted-foreground">
          Producers the A&R world is watching - ranked by how many scouts picked their tracks.
        </p>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
      {!isLoading && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No A&R picks yet. Win battles and get noticed.
        </div>
      )}

      <ol className="space-y-1">
        {items.map((r, i) => (
          <li key={r.producerId}>
            <Link
              to="/$username"
              params={{ username: r.handle }}
              className="flex items-center gap-3 rounded-md border border-border px-3 py-2 hover:border-primary"
            >
              <span className="w-6 text-right font-mono text-sm text-muted-foreground">
                {i + 1}
              </span>
              {r.avatarUrl ? (
                <img src={r.avatarUrl} alt="" className="size-7 rounded-full object-cover" />
              ) : (
                <span className="flex size-7 items-center justify-center rounded-full bg-muted text-xs">
                  {r.handle[0]?.toUpperCase()}
                </span>
              )}
              <span className="flex-1 font-medium">@{r.handle}</span>
              {r.cosigns > 0 && (
                <Badge className="bg-violet-400/15 text-violet-300">{r.cosigns} co-sign</Badge>
              )}
              <span className="font-mono text-[11px] text-muted-foreground">
                {r.arScouts} scout{r.arScouts === 1 ? '' : 's'} · avg {r.avgScore}
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}
